import { cn } from '@rocco/utils/cn';

import type { B2BPageConfig } from '../types';
import { LeadForm } from './LeadForm';

export interface LeadFormSectionProps {
    finalCta: B2BPageConfig['finalCta'];
    copy: B2BPageConfig['leadForm'];
    /** Resolved path for the full-bleed background photograph. */
    backgroundSrc?: string;
    className?: string;
}

/**
 * Page-mode lead form ("Apply to Join"). Rebuilt to Figma node `517:24586` —
 * a full-bleed photographic background with a soft dark wash; the 80 px Neue
 * Haas Roman headline + body copy sit LEFT-aligned in the left column, and a
 * frosted glass card holds the {@link LeadForm} centred to the right of them.
 *
 * Layout follows the 1920-frame coordinate system used across the B2B pages:
 * each child is positioned in % of the 1920 × 1100 section so the composition
 * scales proportionally with any viewport width. Figma slot map:
 *
 * | Slot              | x       | y       | w       | h       |
 * | ----------------- | ------- | ------- | ------- | ------- |
 * | Headline          |   50.5  |  172.48 |  560.65 |   86    |
 * | Body paragraph    |   50.5  |  316.89 |  499.03 |  188    |
 * | Form card         |  719.5  |  170    |  482    |  733    |
 * | Form (in card)    |   40    |   37    |  402    |  659.36 |
 *
 * Mobile (<md) collapses to a vertical stack at fixed type sizes.
 */
export const LeadFormSection = ({
    finalCta,
    copy,
    backgroundSrc,
    className,
}: LeadFormSectionProps) => {
    const headline = finalCta.headline;
    // Two-paragraph body copy on the left: blurb above the closing line.
    const blurb = copy.modalBlurb;
    const closingLine = finalCta.body;

    // Frame height tracks the post-#3433 form (added Business Website,
    // Industry, State+City row). The card itself now hugs the form via
    // natural flow (see below), so the only role of FRAME_H is to anchor
    // the headline/body % positions and the card's top offset.
    //
    // `md:min-h-[60rem]` (960 px) is a floor — the form fields are fixed-px,
    // so at narrow desktop widths (≤1280) the aspect-derived height would
    // be too short to contain the card. min-h prevents that clip without
    // making wide viewports look unnaturally tall.
    const FRAME_H = 1250;
    const CARD_TOP = 170;

    return (
        <section
            className={cn(
                'relative w-full overflow-hidden bg-[#1f1f1c] text-white',
                'md:aspect-[1920/1250] md:min-h-[60rem]',
                className,
            )}
        >
            {backgroundSrc && (
                <div className="absolute inset-0">
                    <img
                        src={backgroundSrc}
                        alt=""
                        className="size-full object-cover"
                        loading="lazy"
                    />
                    {/* Spec overlay rgba(0,0,0,0.1) — keeps the photo legible
                        behind the frosted card without washing it out. */}
                    <div className="absolute inset-0 bg-black/10" />
                </div>
            )}

            {/* Desktop — pixel-positioned to the 1920×1250 Figma frame. */}
            <div className="absolute inset-0 hidden md:block">
                {/* Headline — LEFT column, x=50.5/y=172.48, w=560.65, 80 px
                 *  Neue Haas Roman, line 1.07, tracking -0.048em. */}
                <h2
                    className="absolute text-left font-sans font-normal leading-[1.07] tracking-[-0.048em] text-white"
                    style={{
                        left: `${(50.5 / 1920) * 100}%`,
                        top: `${(172.48 / FRAME_H) * 100}%`,
                        width: `${(560.65 / 1920) * 100}%`,
                        fontSize: `${80 / 19.2}vw`,
                    }}
                >
                    {headline}
                </h2>

                {/* Body — LEFT column, x=50.5/y=316.89, w=499. */}
                {(blurb || closingLine) && (
                    <div
                        className="absolute font-sans font-medium leading-[1.1] tracking-[-0.03em] text-white"
                        style={{
                            left: `${(50.5 / 1920) * 100}%`,
                            top: `${(316.89 / FRAME_H) * 100}%`,
                            width: `${(499.03 / 1920) * 100}%`,
                            fontSize: `${25 / 19.2}vw`,
                        }}
                    >
                        {blurb && <p>{blurb}</p>}
                        {closingLine && (
                            <p className={cn(blurb && 'mt-[1.04vw]')}>
                                {closingLine}
                            </p>
                        )}
                    </div>
                )}

                {/* Frosted form card — x=719.5/y=170, w=482. Card HEIGHT
                 *  used to be fixed at 733/1000 px in design coords, but the
                 *  form fields inside have fixed pixel heights — so at wider
                 *  viewports the card grew taller than the form, leaving a
                 *  big empty band below the submit pill. Now the card hugs
                 *  the form via natural flow (padding instead of absolute
                 *  positioning), so top + bottom padding stay equal at any
                 *  viewport. Padding = 37 px / 19.2 = 1.927vw, matching the
                 *  Figma spec inset. */}
                <div
                    className="absolute rounded-[0.52vw] bg-white/10 px-[2.083vw] py-[1.927vw] backdrop-blur-xl"
                    style={{
                        left: `${(719.5 / 1920) * 100}%`,
                        top: `${(CARD_TOP / FRAME_H) * 100}%`,
                        width: `${(482 / 1920) * 100}%`,
                    }}
                >
                    <LeadForm variant="page" copy={copy} />
                </div>
            </div>

            {/* Mobile — vertical stack with Phase-2 vw tokens.
             *  Headline 5.21 vw (TWK Ghost), body 3.255 vw, gutter 5.4 %,
             *  frosted card padding 4 vw with vw-radius. Submit pill inside
             *  the form will pick up the canonical CTA size from LeadForm. */}
            <div className="relative z-10 flex flex-col gap-[5vw] px-[5.4%] py-[8vw] md:hidden">
                {/* (Lead form keeps inner py because the section has a
                 *  background photo + frosted card that needs breathing
                 *  room from the section's own bleed edges.) */}
                <h2 className="text-center font-twk-ghost text-[5.21vw] leading-[1.07] tracking-[-0.02em] text-white">
                    {headline}
                </h2>
                {(blurb || closingLine) && (
                    <div className="space-y-[2vw] text-[3.255vw] font-medium leading-[1.1] tracking-[-0.030em] text-white">
                        {blurb && <p>{blurb}</p>}
                        {closingLine && <p>{closingLine}</p>}
                    </div>
                )}
                <div className="rounded-[3vw] bg-[rgba(249,246,246,0.2)] p-[4vw] backdrop-blur-md">
                    <LeadForm variant="page" copy={copy} />
                </div>
            </div>
        </section>
    );
};
